// src/pages/users/UsersDetail.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Phone, ArrowLeft, User, Briefcase, MapPin, Target, BookOpen, HelpCircle, Calendar, Hash } from "lucide-react";
import { userDetailApi } from "../../Services/ApiServices";
import { IMG_URL } from "../../Services/Api";
import dummy from "../../assets/Images/dummy.jpeg";
import toast from "react-hot-toast";

const DetailField = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
    <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-2 uppercase tracking-wide">
      {Icon && <Icon size={14} className="text-gray-400" />}
      {label}
    </p>
    <p className="text-sm font-semibold text-gray-900 leading-relaxed">{value || "—"}</p>
  </div>
);

const UsersDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    userDetailApi({
      user_id: id
    })
    .then(res => {
      console.log(res.data);
      setUser(res.data.data);
      setIsLoading(false);
    })
    .catch(err => {
      console.log(err.response.data.message);
      toast.error(err.response.data.message);
      console.log(err);
      setIsLoading(false);
    });
  }, [id]);

  console.log(user);

  if (!user) return <div className="p-10 text-center text-gray-600">Loading user...</div>;

  return (
    <div>
        
        {/* Header with Back Button */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200 border border-gray-200"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Coachee Details</h1>
        </div>

        {/* Personal Information Card - Horizontal Layout */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 mb-6 relative overflow-hidden">

          {/* Horizontal Layout with Profile and Details */}
          <div className="flex flex-col md:flex-row items-start md:items-start gap-6">
            {/* Left Side - Profile Picture */}
            <div className="flex-shrink-0">
              {/* Profile Picture */}
              <div className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden">
                {user.profile_image ? (
          <img
            src={IMG_URL + user?.profile_image || dummy}
                    alt={`${user.firstName} ${user.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-3xl text-gray-400">👤</span>
                  </div>
                )}
          </div>
        </div>

            {/* Right Side - Name and Details Grid */}
            <div className="flex-1 w-full">
              {/* Name */}
              <h2 className="text-lg font-bold text-gray-900 capitalize mb-4 tracking-tight">
                {user?.firstName} {user?.lastName}
              </h2>

              {/* Details in Individual Boxes */}
              <div className="space-y-4">
                {/* Top Row - 4 Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <DetailField label="Phone" value={user?.country_code + " " + user?.phone_number} icon={Phone} />
                  <DetailField label="Relation" value={user?.relation} icon={User} />
                  <DetailField label="Age" value={user?.age} />
                  <DetailField label="Profession" value={user?.profession} icon={Briefcase} />
                </div>
                
                {/* Country, Referred By, Referral Code, and Created At Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <DetailField label="Country" value={user?.country} icon={MapPin} />
                  <DetailField label="Referred By" value={user?.added_by_coach.firstname + " " + user?.added_by_coach.lastname} icon={User} />
                  <DetailField label="Referral Code" value={user?.referral_code} icon={Hash} />
                  <DetailField label="Created At" value={user?.createdAt} icon={Calendar} />
                </div>
                
                {/* Why you think they will benefit from coaching - Full Width */}
                <div className="grid grid-cols-1 gap-4">
                  <DetailField label="Why you think they will benefit from coaching" value={user?.coach_benefit} icon={HelpCircle} />
                </div>
            </div>
            </div>
          </div>
        </div>

        {/* Coaching Details - 6 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          
          {/* Coaching Interests - 3 columns */}
          <div className="md:col-span-3 rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                <Target size={18} className="text-blue-600" />
          </div>
              <span>What would you like to be coached on</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {user?.specialities_list && user?.specialities_list.length > 0 ? (
                user.specialities_list.map((item, idx) => (
                <span
                  key={idx}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border-2 border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-200 transition-all duration-200"
                >
                  {item?.specialities?.specialities}
                </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No interests specified</p>
              )}
            </div>
          </div>

          {/* Coaching Knowledge - 3 columns */}
          <div className="md:col-span-3 rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
                <BookOpen size={18} className="text-green-600" />
              </div>
              <span>What do you know about coaching</span>
            </h3> 
            <div className="flex flex-wrap gap-2">
              {user.selected_about_coaching && user.selected_about_coaching.length > 0 ? (
                user.selected_about_coaching.map((item, idx) => (
                <span
                  key={idx}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border-2 border-green-100 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-200 transition-all duration-200"
                >
                  {item?.about_coaching?.about_coaching}
                </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No knowledge level specified</p>
              )}
            </div>
          </div>

      </div>

    </div>
  );
};

export default UsersDetail;
